import type { EnvironmentResource, GetProjectTemplatesWithValuesBffResponse, ProjectResource, GetScopedCommonTemplatesWithValuesBffResponse, GetConnectedEnvironmentsForTenantedProjectBffResponse } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import type { ProjectTenantVariablesFilterState } from "~/areas/projects/components/Variables/TenantVariables/FiltersBar";
import type { TemplatesWarnings } from "~/areas/projects/components/Variables/TenantVariables/ProjectTenantVariablesNavigationTabs";
import { useCommonTemplatesFilterState } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesFilterState";
import { usePageState } from "~/areas/tenants/components/Paging/usePageState";
import { repository } from "~/clientInstance";
import { type DoBusyTask, useDoBusyTaskEffect } from "~/components/DataBaseComponent/index";
import RequestRaceConditioner from "~/utils/RequestRaceConditioner/index";
interface Actions {
    setPageState: (newPageNumber: number, newPageSize: number) => void;
    setFilter: (newFilter: ProjectTenantVariablesFilterState) => void;
    resetFilter: (newFilter: Partial<ProjectTenantVariablesFilterState>) => void;
    onSave: () => Promise<void>;
}
export interface ScopedCommonTemplatesState {
    data: GetScopedCommonTemplatesWithValuesBffResponse | null;
    connectedEnvironments: Map<string, EnvironmentResource[]>;
    warnings: TemplatesWarnings;
    pageSize: number;
    filterState: ProjectTenantVariablesFilterState;
    actions: Actions;
}
const pagingSelectionKey = "octoProjectTenantVariablesPagingSelection";
export function useScopedCommonTemplatesState(project: ProjectResource, doBusyTask: DoBusyTask): ScopedCommonTemplatesState {
    const [data, setData] = useState<GetScopedCommonTemplatesWithValuesBffResponse | null>(null);
    const [connectedEnvironments, setConnectedEnvironments] = useState<Map<string, EnvironmentResource[]>>(new Map());
    const [warnings, setWarnings] = useState<TemplatesWarnings>({ projectTemplates: false, commonTemplates: false });
    const [pageState, setPageState] = usePageState(pagingSelectionKey);
    const [filterState, setFilterState] = useCommonTemplatesFilterState();
    const [isInitialPageLoad, setIsInitialPageLoad] = useState<boolean>(true);
    const raceConditionerRef: Readonly<React.MutableRefObject<RequestRaceConditioner>> = React.useRef(new RequestRaceConditioner());
    const actions: Actions = {
        setPageState: (newPageNumber, newPageSize) => {
            setFilterState({ ...filterState, pageNumber: newPageNumber });
            setPageState(filterState.pageNumber, newPageSize);
        },
        setFilter: (newFilter) => setFilterState({ ...newFilter, pageNumber: 1 }),
        resetFilter: (newFilter) => setFilterState({ ...defaultFilter, ...newFilter }),
        onSave: async () => {
            await doBusyTask(async () => {
                await raceConditionerRef.current.avoidStaleResponsesForRequest(repository.Projects.getScopedCommonTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState), (response) => {
                    setData(response);
                });
            });
        },
    };
    useDoBusyTaskEffect(doBusyTask, async () => {
        const loadCommonTemplates = () => repository.Projects.getScopedCommonTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState);
        const getConnectedEnvironments = () => repository.Projects.getConnectedEnvironments(project.Id);
        const updateDataState = (commonResponse: GetScopedCommonTemplatesWithValuesBffResponse | null, projectResponse: GetProjectTemplatesWithValuesBffResponse | null, connectedEnvironmentsResponse: GetConnectedEnvironmentsForTenantedProjectBffResponse | null) => {
            setData(commonResponse);
            if (connectedEnvironmentsResponse) {
                const environmentsMap = new Map<string, EnvironmentResource[]>();
                connectedEnvironmentsResponse?.ConnectedEnvironments.forEach((connectedEnvironment) => {
                    environmentsMap.set(connectedEnvironment.TenantId, connectedEnvironment.Environments);
                });
                setConnectedEnvironments(environmentsMap);
            }
            setWarnings((previous) => ({ commonTemplates: commonResponse?.AnyMissingValues ?? previous.commonTemplates, projectTemplates: projectResponse?.AnyMissingValues ?? previous.projectTemplates }));
        };
        if (isInitialPageLoad) {
            const [commonResponse, projectResponse, connectedEnvironmentsResponse] = await Promise.all([
                loadCommonTemplates(),
                repository.Projects.getProjectTemplatesWithValues(project.Id, (filterState.pageNumber - 1) * pageState.pageSize, pageState.pageSize, filterState),
                getConnectedEnvironments(),
            ]);
            updateDataState(commonResponse, projectResponse, connectedEnvironmentsResponse);
            setIsInitialPageLoad(false);
        }
        else {
            await raceConditionerRef.current.avoidStaleResponsesForRequest(loadCommonTemplates(), (response) => updateDataState(response, null, null));
        }
    }, [project, filterState, pageState.pageSize]);
    return { data, connectedEnvironments, warnings, pageSize: pageState.pageSize, filterState, actions };
}
const defaultFilter: ProjectTenantVariablesFilterState = {
    filterByName: undefined,
    filterByEnvironment: undefined,
    filterByExcludedEnvironment: undefined,
    filterByTenant: undefined,
    filterByExcludedTenant: undefined,
    filterByTags: [],
    filterByExcludedTags: [],
    filterByValueStatus: undefined,
    filterByTemplateId: undefined,
    filterByLibraryVariableSetId: undefined,
    filterByExcludedLibraryVariableSetId: undefined,
    filterByProjectId: undefined,
    filterByExcludedProjectId: undefined,
    pageNumber: 1,
};
